<script>
  import { SPRITESHEET_URL } from '../../utils/constants';
  import BaseHeading from '../BaseHeading/BaseHeading.svelte';
  import BaseLink from '../BaseLink/BaseLink.svelte';
  import { getPath } from '../../utils/imageKit.ts';

  let {
    headingLevel = 'h2',
    headingText = undefined,
    moreLinkText = undefined,
    moreLinkTextTemplate = undefined,
    moreLink = undefined,
    articles = $bindable([]),
    dataTestId = 'articles-promo',
    inline = true,
    isBlogHome = false,
    columns = 2,
    isPromo = false,
  } = $props();

  // Handle Image Setup
  articles.forEach(({ image }, index) => {
    articles[index].image.srcset = {};
    if (inline || (articles.length === 3 && (index === 1 || index === 2))) {
      articles[index].image.srcset.defaultSrc = getPath(image, { w: 128 });
      articles[index].image.srcset.highResSrc = getPath(image, { w: 128 }, 2);
      if (inline) {
        articles[index].image.srcset.xlDefaultSrc = getPath(image, { w: 144 });
        articles[index].image.srcset.xlHighResSrc = getPath(image, { w: 144 }, 2);
      } else {
        articles[index].image.srcset.xlDefaultSrc = getPath(image, { w: 352 });
        articles[index].image.srcset.xlHighResSrc = getPath(image, { w: 352 }, 2);
      }
    } else {
      articles[index].image.srcset.defaultSrc = getPath(image, { w: 374 });
      articles[index].image.srcset.highResSrc = getPath(image, { w: 374 }, 2);
      articles[index].image.srcset.mobileDefaultSrc = getPath(image, { w: 592 });
      articles[index].image.srcset.mobileHighResSrc = getPath(image, { w: 592 }, 2);
      articles[index].image.srcset.tabletDefaultSrc = getPath(image, { w: 720 });
      articles[index].image.srcset.tabletHighResSrc = getPath(image, { w: 720 }, 2);
      articles[index].image.srcset.desktopDefaultSrc = getPath(image, { w: 476 });
      articles[index].image.srcset.desktopHighResSrc = getPath(image, { w: 476 }, 2);
      articles[index].image.srcset.xlDefaultSrc = getPath(image, { w: 352 });
      articles[index].image.srcset.xlHighResSrc = getPath(image, { w: 352 }, 2);
    }
  });
</script>

<div
  class:max-w-6xl={isPromo}
  class:mx-auto={isPromo}
  class:px-4={isPromo}
  class:py-8={isPromo}
  class:lg:py-16={isPromo}
  data-testid={dataTestId}
>
  {#if headingText}
    <div class="mb-4 md:mb-6 pb-3 border-b border-gray-light-silver">
      {#if moreLink}
        <BaseLink className="text-blue-cerulean no-underline flex items-center group" {...moreLink}>
          <BaseHeading
            className={`grow text-lg leading-tight ${
              isBlogHome
                ? 'lg:text-xl font-light text-gray-charcoal'
                : 'md:text-xl lg:text-2xl font-medium text-blue-dark-navy'
            }`}
            {headingLevel}
          >
            {headingText}
          </BaseHeading>
          {#if moreLinkText}
            <span
              class="hidden sm:block font-medium text-0.5sm hover-hover:group-hover:text-blue-dark-navy hover-hover:group-hover:underline"
              class:md:block={columns === 1}
              class:md:hidden={columns > 1 && moreLinkTextTemplate}
            >
              {moreLinkText}
            </span>
          {/if}
          {#if moreLinkTextTemplate}
            <span
              class="hidden font-medium text-0.5sm hover-hover:group-hover:text-blue-dark-navy hover-hover:group-hover:underline"
              class:md:block={columns > 1}
            >
              {moreLinkTextTemplate}
            </span>
          {/if}
          <svg
            class="w-4 h-4 fill-current ml-2 hover-hover:group-hover:text-blue-dark-navy active:text-blue-dark-navy"
            role="presentation"
          >
            <use xlink:href="{SPRITESHEET_URL}#right-arrow" />
          </svg>
        </BaseLink>
      {:else}
        <BaseHeading
          className={`grow text-lg leading-tight ${
            isBlogHome
              ? 'lg:text-xl font-light text-gray-charcoal'
              : 'md:text-xl lg:text-2xl font-medium text-blue-dark-navy'
          }`}
          {headingLevel}
        >
          {headingText}
        </BaseHeading>
      {/if}
    </div>
  {/if}
  <div
    class="grid gap-y-6 gap-x-4 sm:gap-x-6 xl:gap-x-8"
    class:md:grid-cols-2={columns === 2 || (articles.length === 3 && !inline)}
    class:lg:grid-cols-3={!inline}
  >
    {#each articles as article, index}
      <BaseLink
        className={`group text-blue-navy no-underline ${
          (inline || (articles.length === 3 && (index === 1 || index === 2))) && 'flex'
        } 
      ${!inline && articles.length === 3 && (index === 1 || index === 2) && 'lg:block'}
      ${!inline && articles.length === 3 && index === 0 && 'md:row-span-2 lg:row-span-1'} 
      ${!inline && articles.length === 3 && index === 2 && 'md:col-start-2 lg:col-start-3'}`}
        {...article.link}
      >
        {#if article.image && article.image.url && article.image.alt}
          <picture
            class={`min-w-32 flex print:hidden items-center justify-center overflow-hidden`}
            class:w-32={inline || (articles.length === 3 && (index === 1 || index === 2))}
            class:lg:min-w-36={inline}
            class:lg:w-36={inline}
            class:md:h-24={inline || (articles.length === 3 && (index === 1 || index === 2))}
            class:h-22={inline || (articles.length === 3 && (index === 1 || index === 2))}
            class:mr-4={inline || (articles.length === 3 && (index === 1 || index === 2))}
            class:lg:mr-0={!inline}
            class:mb-3={!inline && index !== 1 && index !== 2}
            class:lg:mb-3={!inline}
            class:h-48={!inline && articles.length === 3 && index === 0}
            class:lg:w-auto={!inline}
            class:lg:h-48={!inline}
            class:max-h-48={!inline}
            class:md:max-h-88={!inline}
            class:bg-blue-bright-cyan={article.theme === 'light_blue'}
            class:bg-pink-light={article.theme === 'light_pink'}
            class:bg-blue-darkish={article.theme === 'dark_blue'}
            class:bg-gray-darkest={article.theme === 'dark_gray'}
            class:bg-purple-light={article.theme === 'light_purple'}
            class:bg-yellow-gold={article.theme === 'gold'}
            data-testid="{dataTestId}-image-wrapper"
          >
            <source
              media="(min-width: 1024px)"
              srcset={`${article.image.srcset.xlDefaultSrc}, ${article.image.srcset.xlHighResSrc} 2x`}
            />
            {#if article.image.srcset.mobileDefaultSrc}
              <source
                media="(min-width: 768px)"
                srcset={`${article.image.srcset.desktopDefaultSrc}, ${article.image.srcset.desktopHighResSrc} 2x`}
              />
              <source
                media="(min-width: 640px)"
                srcset={`${article.image.srcset.tabletDefaultSrc}, ${article.image.srcset.tabletHighResSrc} 2x`}
              />
              <source
                media="(min-width: 422px)"
                srcset={`${article.image.srcset.mobileDefaultSrc}, ${article.image.srcset.mobileHighResSrc} 2x`}
              />
              <source
                media="(max-width: 421px)"
                srcset={`${article.image.srcset.defaultSrc}, ${article.image.srcset.highResSrc} 2x`}
              />
            {:else}
              <source
                media="(max-width: 1023px)"
                srcset={`${article.image.srcset.defaultSrc}, ${article.image.srcset.highResSrc} 2x`}
              />
            {/if}
            <img
              class={`object-cover h-full w-full duration-75 transition-transform ease-in hover-hover:group-hover:scale-105 group-active:scale-105 ${
                article.image && article.image.imagePosition
                  ? article.image.imagePosition.replace(/_/g, '-')
                  : 'object-center'
              }`}
              loading={article.lazyloadImage && article.lazyloadImage === false ? 'eager' : 'lazy'}
              src={article.image.srcset.defaultSrc}
              alt={article.image.alt}
              height="88"
              width="128"
            />
          </picture>
        {/if}
        <div>
          <span
            class="block text-gray-warm text-0.5xs font-medium leading-relaxed tracking-wide uppercase lg:mt-1"
            data-testid="{dataTestId}-category-name"
            >{article.keywordsSubheading ? article.keywordsSubheading : article.category.name}</span
          >
          <h3
            class="hover-hover:group-hover:underline hover-hover:group-hover:text-blue-cerulean active:underline
        active:text-blue-cerulean sm:mt-1 lg:mt-2 font-medium text-base"
          >
            {article.title}
          </h3>
        </div>
      </BaseLink>
    {/each}
  </div>
</div>
